// Телефоны человека и проба уведомлений: то, чем администратор за минуту узнаёт, почему у
// сотрудника молчит телефон.
//
// До 01.10.2026 про молчащее уведомление нельзя было узнать ничего, кроме «не приходит»:
// причину («не разрешили», «нет ключа», «адрес от другой среды», «приложение переустановили»)
// знал только сервер. Теперь сервер сам пробует отправить и отвечает по каждому телефону
// словами. Слова здесь НЕ выдумываются: `message`, `summary` и `next_action` приходят готовыми,
// и экран показывает их как есть (`server/app/services/devices.py`).
//
// Адреса доставки в ответах нет и не будет: это ключ к телефону человека, получивший его шлёт
// туда что угодно от имени системы. Поэтому его нет и в этих типах — показывать нечего.

import { request } from '@/shared/api/client';

/**
 * Телефон человека. Зеркало серверного `DeviceOut` (`server/app/serializers/devices.py`).
 *
 * Отцеплённые (`is_active: false`) приходят тоже: по ним и видно, почему телефон молчит.
 */
export interface DeviceOut {
  id: string;
  /** `ios`, `android` или `web`. Строкой, а не перечислением: список растёт на сервере. */
  platform: string;
  /** Версия приложения на телефоне. Пусто у тех, кто её не прислал. */
  app_version: string | null;
  /** Как телефон назвал себя: «iPhone», «samsung SM-A546E». Пусто — не назвал. */
  device_name: string | null;
  /** Адрес на месте и не отцеплён. Принимает ли его площадка, знает только проба. */
  is_active: boolean;
  registered_at: string;
  /**
   * Когда телефон в последний раз сообщил «Барку» свой адрес.
   *
   * Это НЕ «когда человек последний раз заходил в приложение»: сервер двигает время только
   * при регистрации адреса, а стоящие у людей версии приложения один раз отправленный адрес
   * повторно не шлют (`repositories/devices.py`, `PushRegistrar.offer`).
   */
  last_seen_at: string;
}

/**
 * Чем кончилась проба на один телефон. Закрытый список сервера: по нему подбирается цвет
 * отметки, а разбирать текст причины экрану не нужно.
 */
export type DeviceTestResult =
  | 'sent'
  | 'address_outdated'
  | 'wrong_environment'
  | 'not_configured'
  | 'server_key_problem'
  | 'network'
  | 'rate_limited'
  | 'inactive'
  | 'unsupported'
  | 'failed';

/** Телефон и итог пробы на нём. */
export interface DeviceTestItem extends DeviceOut {
  result: DeviceTestResult;
  /** Что произошло, словами для человека. Показывается как есть. */
  message: string;
}

/** Ответ на «Проверить уведомления». */
export interface DeviceTestOut {
  /** Номер пробы: приходит и в самом уведомлении, по нему приложение узнаёт своё пробное. */
  test_id: string;
  /** Одна строка: что получилось. */
  summary: string;
  /** Что сделать дальше, если что-то не так. Пусто — делать нечего. */
  next_action: string | null;
  devices: DeviceTestItem[];
}

/** Телефоны человека (право `core.user.view`). Массив, а не `{items}`: так отвечает сервер. */
export function listUserDevices(userId: string, signal?: AbortSignal): Promise<DeviceOut[]> {
  return request<DeviceOut[]>(`/api/v1/users/${userId}/devices`, { signal });
}

/**
 * Проверить уведомления на ВСЕХ живых телефонах человека (право `core.user.manage` либо
 * `core.user.access`). Тело не нужно. Никого не отцепляет: проба только смотрит.
 *
 * Чаще раза в десять секунд сервер не пускает и отвечает 429 с обычным конвертом ошибки; для
 * человека вне области видимости — 404.
 */
export function testUserDevices(userId: string): Promise<DeviceTestOut> {
  return request<DeviceTestOut>(`/api/v1/users/${userId}/devices/test`, { method: 'POST' });
}
